/*
 * Live feedback controller — ported from the live polling logic in taskpane.js
 * (startLiveFeedback / stopLiveFeedback / pollLiveParagraph / scanLiveParagraph /
 * accept/skip / requestLiveRescan).
 *
 * The original kept its polling state on the global `state` object and ran for
 * the page lifetime regardless of the visible tab. To preserve that cross-tab
 * behavior in React, the timers + sequence guards live as module singletons
 * here (one live session per task pane) instead of in a panel-bound hook.
 * Render data is published to the Zustand store; status text goes to
 * store.liveStatus. The stale-response requestId guard is unchanged.
 */

import { useStore } from "../state/store";
import { getDict } from "../i18n/translations";
import { getLiveRules, resolveRuleSelection } from "../rules/ruleLogic";
import { readCurrentParagraphText } from "../office/document";
import { applyLiveSuggestionToCurrentParagraph } from "../office/live";
import { scanRepetitions, scanTextSuggestions } from "../api/client";
import type { Rule } from "../state/types";

let pollTimer: number | null = null;
let debounceTimer: number | null = null;
let requestSeq = 0;
let lastObservedText = "";
let lastObservedSignature = "";
let lastScannedText = "";
let lastScannedSignature = "";

function startPolling(): void {
  if (pollTimer !== null) return;
  const s = useStore.getState();
  lastObservedText = "";
  lastObservedSignature = "";
  lastScannedText = "";
  lastScannedSignature = "";
  requestSeq++;
  setStatus(s.liveEnabled ? dict().liveIdle : "");
  void poll();
  pollTimer = window.setInterval(() => void poll(), 1250);
}

function stopPolling(): void {
  if (pollTimer !== null) window.clearInterval(pollTimer);
  if (debounceTimer !== null) window.clearTimeout(debounceTimer);
  pollTimer = null;
  debounceTimer = null;
  requestSeq++;
  useStore.getState().patch({
    liveIsScanning: false,
    liveSuggestions: [],
    currentLiveSuggestionIndex: -1,
    liveRepetitions: [],
  });
  setStatus(dict().livePrompt);
}

function dict() {
  return getDict(useStore.getState().lang);
}

function setStatus(message: string, variant: "idle" | "error" = "idle"): void {
  useStore.getState().patch({ liveStatus: { message, variant } });
}

function getSelectedLiveRuleIds(rules: Rule[], selectedRuleIds: string[]): string[] {
  const liveRuleIds = new Set(getLiveRules(rules).map((rule) => rule.id));
  return resolveRuleSelection(
    rules,
    selectedRuleIds.filter((ruleId) => liveRuleIds.has(ruleId))
  );
}

function repetitionOptions(): { windowSize: number; minOccurrences: number } {
  const s = useStore.getState();
  return { windowSize: s.liveRepetitionWindow, minOccurrences: s.liveRepetitionFrequency };
}

function buildSignature(ruleIds: string[]): string {
  const s = useStore.getState();
  return JSON.stringify({
    ruleIds: [...ruleIds].sort(),
    preferences: s.preferences,
    repetitionEnabled: s.liveRepetitionEnabled,
    repetitionOptions: s.liveRepetitionEnabled ? repetitionOptions() : null,
  });
}

function clampInt(value: number, fallback: number, min: number, max: number): number {
  const numeric = Math.trunc(Number(value));
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
}

async function poll(): Promise<void> {
  const s = useStore.getState();
  if ((!s.liveEnabled && !s.liveRepetitionEnabled) || s.liveIsScanning) return;

  const ruleIds = getSelectedLiveRuleIds(s.rules, s.selectedRuleIds);
  if (ruleIds.length === 0 && !s.liveRepetitionEnabled) {
    setStatus(dict().liveNoEligibleRules, "error");
    return;
  }

  try {
    const paragraphText = await readCurrentParagraphText();
    const signature = buildSignature(ruleIds);
    if (!paragraphText.trim()) {
      useStore.getState().patch({ liveSuggestions: [], currentLiveSuggestionIndex: -1 });
      setStatus(dict().liveIdle);
      return;
    }

    if (paragraphText === lastObservedText && signature === lastObservedSignature) {
      return;
    }

    lastObservedText = paragraphText;
    lastObservedSignature = signature;
    useStore.getState().patch({ liveSuggestions: [], currentLiveSuggestionIndex: -1 });
    setStatus(dict().liveWaiting);
    if (debounceTimer !== null) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      void scan(paragraphText, ruleIds, signature);
    }, 2000);
  } catch {
    setStatus(dict().liveUnsupported, "error");
  }
}

async function scan(paragraphText: string, ruleIds: string[], signature: string): Promise<void> {
  if (!useStore.getState().liveEnabled && !useStore.getState().liveRepetitionEnabled) return;
  if (paragraphText === lastScannedText && signature === lastScannedSignature) return;

  const requestId = ++requestSeq;
  const s = useStore.getState();
  s.patch({ liveIsScanning: true });
  setStatus(dict().liveScanning);

  const editorialPromise =
    ruleIds.length > 0 && s.liveEnabled
      ? scanTextSuggestions(s.token, paragraphText, ruleIds, s.preferences)
      : Promise.resolve({ changes: [] });
  const repetitionPromise = s.liveRepetitionEnabled
    ? scanRepetitions(s.token, paragraphText, repetitionOptions())
    : Promise.resolve({ repetitions: [] });

  try {
    const [editorialResult, repetitionResult] = await Promise.allSettled([
      editorialPromise,
      repetitionPromise,
    ]);
    if (
      (!useStore.getState().liveEnabled && !useStore.getState().liveRepetitionEnabled) ||
      requestId !== requestSeq
    )
      return;

    lastScannedText = paragraphText;
    lastScannedSignature = signature;

    const currentLiveEnabled = useStore.getState().liveEnabled;
    const suggestions =
      currentLiveEnabled && editorialResult.status === "fulfilled"
        ? editorialResult.value?.changes || []
        : [];
    const repetitions =
      useStore.getState().liveRepetitionEnabled && repetitionResult.status === "fulfilled"
        ? repetitionResult.value?.repetitions || []
        : [];

    useStore.getState().patch({
      liveParagraphText: paragraphText,
      liveSuggestions: suggestions,
      currentLiveSuggestionIndex: suggestions.length > 0 ? 0 : -1,
      liveRepetitions: repetitions,
    });

    if (suggestions.length > 0 || repetitions.length > 0) {
      setStatus("");
    } else {
      setStatus(dict().liveNoIssues);
    }

    const firstError =
      editorialResult.status === "rejected"
        ? editorialResult.reason
        : repetitionResult.status === "rejected"
          ? repetitionResult.reason
          : null;
    if (firstError && suggestions.length === 0 && repetitions.length === 0) {
      const msg = firstError instanceof Error ? firstError.message : String(firstError);
      setStatus(`${dict().statusError} ${msg}`, "error");
    }
  } catch (err) {
    if (requestId === requestSeq) {
      setStatus(
        `${dict().statusError} ${err instanceof Error ? err.message : String(err)}`,
        "error"
      );
    }
  } finally {
    if (requestId === requestSeq) {
      useStore.getState().patch({ liveIsScanning: false });
    }
  }
}

export const liveController = {
  start(): void {
    useStore.getState().patch({ liveEnabled: true });
    startPolling();
  },

  stop(): void {
    useStore.getState().patch({
      liveEnabled: false,
      liveIsScanning: false,
      liveSuggestions: [],
      currentLiveSuggestionIndex: -1,
    });
    if (!useStore.getState().liveRepetitionEnabled) {
      stopPolling();
    }
  },

  rescan(): void {
    const s = useStore.getState();
    if (!s.liveEnabled && !s.liveRepetitionEnabled) return;
    lastObservedText = "";
    lastObservedSignature = "";
    void poll();
  },

  skip(): void {
    const s = useStore.getState();
    const next = s.currentLiveSuggestionIndex + 1;
    s.patch({ currentLiveSuggestionIndex: next });
    if (next >= s.liveSuggestions.length) {
      setStatus(dict().liveNoIssues);
    }
  },

  async accept(): Promise<void> {
    const s = useStore.getState();
    const change = s.liveSuggestions[s.currentLiveSuggestionIndex];
    if (!change) return;

    s.setLoading(true);
    try {
      const appliedText = await applyLiveSuggestionToCurrentParagraph(
        change,
        dict().liveStaleSuggestion,
        dict().liveUnsupported
      );
      lastObservedText = appliedText;
      lastScannedText = "";
      useStore.getState().patch({
        liveParagraphText: appliedText,
        liveSuggestions: [],
        currentLiveSuggestionIndex: -1,
      });
      setStatus(dict().liveWaiting);
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        const fresh = useStore.getState();
        const ruleIds = getSelectedLiveRuleIds(fresh.rules, fresh.selectedRuleIds);
        void scan(appliedText, ruleIds, buildSignature(ruleIds));
      }, 500);
    } catch (err) {
      setStatus(
        `${dict().statusError} ${err instanceof Error ? err.message : String(err)}`,
        "error"
      );
    } finally {
      useStore.getState().setLoading(false);
    }
  },

  setRepetitionEnabled(enabled: boolean): void {
    useStore.getState().patch({ liveRepetitionEnabled: enabled });
    if (enabled) {
      if (!useStore.getState().liveEnabled && pollTimer === null) {
        startPolling();
      } else {
        this.rescan();
      }
    } else {
      if (!useStore.getState().liveEnabled) {
        stopPolling();
      } else {
        this.rescan();
      }
    }
  },

  setRepetitionConfig(windowValue: number, frequencyValue: number): void {
    useStore.getState().patch({
      liveRepetitionWindow: clampInt(windowValue, 50, 1, 250),
      liveRepetitionFrequency: clampInt(frequencyValue, 2, 2, 20),
    });
    this.rescan();
  },
};
